import React, { useState, useEffect, useMemo } from 'react';
import {
  TrendingUp, Users, Car, DollarSign,
  Clock, MapPin, Calendar, Activity,
  ArrowUp, ArrowDown, Minus, RefreshCw
} from 'lucide-react';
import { getStats, getDrivers, getRides } from '../../services/api';

const HeatmapCell = ({ value, maxValue, label }) => {
  const intensity = maxValue > 0 ? value / maxValue : 0;

  let cellClass = 'bg-gray-100 text-gray-400';
  if (value > 0) {
    if (intensity > 0.66) cellClass = 'bg-amber-500 text-gray-900';
    else if (intensity > 0.33) cellClass = 'bg-amber-300 text-gray-900';
    else cellClass = 'bg-amber-100 text-gray-800';
  }

  return (
    <div
      className={`w-full h-8 rounded flex items-center justify-center text-xs font-semibold transition-all hover:ring-2 hover:ring-amber-400/50 ${cellClass}`}
      title={`${label}: ${value} udhëtime`}
    >
      {value > 0 ? value : ''}
    </div>
  );
};

const StatCard = ({ title, value, trend, trendLabel, icon: Icon, iconBg, iconColor, trendUp }) => (
  <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-center justify-between">
      <div className="min-w-0">
        <p className="text-sm text-gray-500">{title}</p>
        <p className="text-2xl font-bold text-gray-900 mt-1 tabular-nums">{value}</p>
        {trendLabel && (
          <div className={`flex items-center gap-1 mt-2 text-xs ${
            trendUp === true ? 'text-emerald-600' :
            trendUp === false ? 'text-red-500' :
            'text-gray-500'
          }`}>
            {trendUp === true && <ArrowUp className="w-3 h-3" />}
            {trendUp === false && <ArrowDown className="w-3 h-3" />}
            {trendUp === null && <Minus className="w-3 h-3" />}
            <span>{trendLabel}</span>
          </div>
        )}
      </div>
      <div className={`w-12 h-12 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-6 h-6 ${iconColor}`} />
      </div>
    </div>
  </div>
);

const RankBadge = ({ index }) => {
  const styles = [
    'bg-amber-400 text-gray-900',
    'bg-gray-300 text-gray-800',
    'bg-amber-700/80 text-white',
    'bg-gray-200 text-gray-700',
  ];
  return (
    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${styles[index] || styles[3]}`}>
      {index + 1}
    </span>
  );
};

const AnalyticsPage = () => {
  const [stats, setStats] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('week');

  useEffect(() => {
    fetchData();
  }, [period]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsData, driversData, ridesData] = await Promise.all([
        getStats(),
        getDrivers(),
        getRides()
      ]);
      setStats(statsData);
      setDrivers(driversData || []);
      setRides(ridesData || []);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const hourlyDistribution = useMemo(() => {
    const hours = Array(24).fill(0);
    rides.forEach(ride => {
      if (ride.created_at) {
        const hour = new Date(ride.created_at).getHours();
        hours[hour]++;
      }
    });
    return hours;
  }, [rides]);

  const weeklyDistribution = useMemo(() => {
    const days = ['Hënë', 'Martë', 'Mërkurë', 'Enjte', 'Premte', 'Shtunë', 'Diel'];
    const distribution = Array(7).fill(0);

    rides.forEach(ride => {
      if (ride.created_at) {
        let day = new Date(ride.created_at).getDay();
        day = day === 0 ? 6 : day - 1;
        distribution[day]++;
      }
    });

    return days.map((name, i) => ({ name, count: distribution[i] }));
  }, [rides]);

  const topDestinations = useMemo(() => {
    const destinations = {};
    rides.forEach(ride => {
      const dest = ride.destination || ride.dropoff?.address || 'I panjohur';
      destinations[dest] = (destinations[dest] || 0) + 1;
    });

    return Object.entries(destinations)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, count]) => ({ name, count }));
  }, [rides]);

  const driverPerformance = useMemo(() => {
    return drivers
      .filter(d => d.total_rides > 0)
      .sort((a, b) => (b.total_earnings || 0) - (a.total_earnings || 0))
      .slice(0, 5);
  }, [drivers]);

  const formatPrice = (amount) => {
    return Number(amount || 0).toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  const maxHourlyValue = Math.max(...hourlyDistribution, 1);
  const maxWeeklyValue = Math.max(...weeklyDistribution.map(d => d.count), 1);
  const activeDrivers = drivers.filter(d => d.status !== 'offline').length;
  const avgPrice = formatPrice((stats?.total_earnings || 0) / Math.max(rides.length, 1));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-500">Duke ngarkuar analitikën...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analitika</h1>
          <p className="text-gray-500 text-sm mt-1">Statistika dhe raporte të detajuara</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-white border border-gray-200 rounded-lg p-1 shadow-sm">
            {['week', 'month'].map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${
                  period === p
                    ? 'bg-amber-500 text-gray-900'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {p === 'week' ? 'Java' : 'Muaji'}
              </button>
            ))}
          </div>
          <button
            onClick={fetchData}
            className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition shadow-sm"
            aria-label="Rifresko"
          >
            <RefreshCw className="w-5 h-5 text-gray-500" />
          </button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard
          title="Të Ardhura Totale"
          value={`${formatPrice(stats?.total_earnings || 0)} L`}
          trendLabel="+12.5% nga java kaluar"
          trendUp={true}
          icon={DollarSign}
          iconBg="bg-emerald-50"
          iconColor="text-emerald-600"
        />
        <StatCard
          title="Udhëtime Totale"
          value={rides.length}
          trendLabel="+8.2% nga java kaluar"
          trendUp={true}
          icon={Car}
          iconBg="bg-blue-50"
          iconColor="text-blue-600"
        />
        <StatCard
          title="Shoferë Aktivë"
          value={activeDrivers}
          trendLabel="Njësoj si dje"
          trendUp={null}
          icon={Users}
          iconBg="bg-amber-50"
          iconColor="text-amber-600"
        />
        <StatCard
          title="Çmimi Mesatar"
          value={`${avgPrice} L`}
          trendLabel="+3.1% nga java kaluar"
          trendUp={true}
          icon={Activity}
          iconBg="bg-violet-50"
          iconColor="text-violet-600"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
          <h3 className="text-gray-900 font-semibold mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-500" />
            Aktiviteti sipas Orës
          </h3>
          <div className="grid grid-cols-12 gap-1">
            {hourlyDistribution.map((count, hour) => (
              <div key={hour} className="flex flex-col items-center">
                <HeatmapCell
                  value={count}
                  maxValue={maxHourlyValue}
                  label={`${hour}:00`}
                />
                <span className="text-[10px] text-gray-400 mt-1">{hour}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-center gap-4 mt-4 pt-3 border-t border-gray-100">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <div className="w-4 h-4 rounded bg-gray-100 border border-gray-200" />
              <span>I ulët</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <div className="w-4 h-4 rounded bg-amber-100" />
              <span>Mesatar</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <div className="w-4 h-4 rounded bg-amber-500" />
              <span>I lartë</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
          <h3 className="text-gray-900 font-semibold mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-amber-500" />
            Aktiviteti sipas Ditës
          </h3>
          <div className="space-y-3">
            {weeklyDistribution.map((day, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="w-20 text-sm text-gray-600 font-medium">{day.name}</span>
                <div className="flex-1 h-7 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-amber-400 to-amber-500 transition-all"
                    style={{
                      width: `${maxWeeklyValue > 0 ? (day.count / maxWeeklyValue) * 100 : 0}%`,
                      minWidth: day.count > 0 ? '8px' : '0',
                    }}
                  />
                </div>
                <span className="w-10 text-sm font-semibold text-gray-800 text-right tabular-nums">
                  {day.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
          <h3 className="text-gray-900 font-semibold mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-amber-500" />
            Destinacionet më Popullore
          </h3>
          {topDestinations.length === 0 ? (
            <p className="text-gray-400 text-center py-8">Nuk ka të dhëna</p>
          ) : (
            <div className="space-y-2">
              {topDestinations.map((dest, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100 hover:bg-gray-100/80 transition-colors"
                >
                  <RankBadge index={index} />
                  <span className="flex-1 text-gray-800 truncate">{dest.name}</span>
                  <span className="text-sm font-bold text-gray-900 tabular-nums bg-white px-2.5 py-1 rounded-md border border-gray-200">
                    {dest.count}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
          <h3 className="text-gray-900 font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-amber-500" />
            Shoferët më të Mirë
          </h3>
          {driverPerformance.length === 0 ? (
            <p className="text-gray-400 text-center py-8">Nuk ka të dhëna</p>
          ) : (
            <div className="space-y-2">
              {driverPerformance.map((driver, index) => (
                <div
                  key={driver.id}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100 hover:bg-gray-100/80 transition-colors"
                >
                  <RankBadge index={index} />
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
                    {driver.avatar || driver.name?.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-900 font-medium truncate">{driver.name}</p>
                    <p className="text-xs text-gray-500">{driver.total_rides} udhëtime</p>
                  </div>
                  <span className="text-sm font-bold text-emerald-700 tabular-nums whitespace-nowrap">
                    {formatPrice(driver.total_earnings)} L
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
